import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const DICT = {
  en: {
    // header / nav
    "nav.compare": "Compare two options",
    "nav.single": "Single analysis",
    "nav.start": "Start analysis",
    // hero
    "hero.eyebrow": "A boardroom-ready decision tool",
    "hero.h1a": "Prove the ROI of a press release",
    "hero.h1b": " — before you spend a euro.",
    "hero.body": "A rigorous calculator that turns soft PR assumptions into a decision-ready dashboard. Live ranges, sensitivity, sourced benchmarks, ECB-backed currency and an AI-written executive summary — all in a shareable link built for the boardroom.",
    "hero.cta": "Start a new ROI analysis",
    "hero.compare": "Or compare two options side-by-side",
    "hero.see": "See how the model works",
    "hero.stat.inputs": "Quick mode",
    "hero.stat.range": "Sensitivity band",
    "hero.stat.model": "Model type toggle",
    "hero.stat.share": "Shareable link",
    // calculator
    "calc.currency": "Currency",
    "calc.generate": "Generate report",
    "calc.generating": "Generating…",
    "calc.section.model": "Model",
    "calc.section.mode": "Mode",
    "calc.section.basics": "Campaign basics",
    "calc.section.basics.sub": "Context for the ROI narrative",
    "calc.section.tc": "Traffic & Conversion",
    "calc.section.tc.sub": "The six numbers that move ROI the most",
    "calc.section.costs": "Costs",
    "calc.section.costs.sub": "Everything you'll spend on this release",
    "calc.section.bench": "Benchmarks (advanced)",
    "calc.section.bench.sub": "Override any pre-filled default. Blank = keep benchmark.",
    "calc.results.title": "Live Results",
    "calc.results.empty": "Enter your inputs to see the ROI projection.",
    "calc.roi": "Base-case ROI",
    "calc.net": "Net benefit",
    "calc.payback": "Payback",
    "calc.total_benefit": "Total benefit",
    "calc.total_cost": "Total cost",
    "calc.range": "Range (±30% flex)",
    "calc.benefits": "Benefit breakdown",
    "calc.drivers": "Top value drivers",
    // report layers
    "report.layer1": "Scorecard",
    "report.layer1.sub": "Fifteen seconds to a headline verdict",
    "report.layer2": "Details for finance",
    "report.layer2.sub": "Sourced benchmarks · ECB currency · full assumptions",
    "report.scorecard.cost": "Cost Efficiency",
    "report.scorecard.sov": "Share of Voice",
    "report.scorecard.reach": "Media Reach",
    "report.exec": "Executive Summary",
    "report.recommendation": "Recommendation",
    "report.benefits": "Benefit Breakdown",
    "report.benefits.h": "Where the value comes from",
    "report.sensitivity": "Sensitivity Analysis",
    "report.sensitivity.h": "Conservative → Base → Optimistic",
    "report.assumptions": "Assumptions",
    "report.assumptions.h": "All inputs & benchmarks used",
    "report.drivers": "Top 3 value drivers",
    "report.sensitive": "Most sensitive assumptions",
    "report.copy": "Copy link",
    "report.print": "Print",
    "report.email": "Email report",
    "report.nordic_fallback": "Nordic-specific data pending licence — using European average as fallback.",
    // email modal
    "email.title": "Where should we send the report?",
    "email.body": "Every analysis is saved to a shareable link. Add your work email to receive it and unlock delivery to your stakeholders.",
    "email.placeholder": "you@company.com",
    "email.confirm": "Generate & save",
    "email.skip": "Skip — just show the link",
    "email.quota": "Free tier · {remaining} of {limit} reports remaining",
    // paywall
    "paywall.title": "Free tier reached",
    "paywall.body": "You've used {used} of {limit} free reports for this email. Upgrade to continue analysing.",
    "paywall.cta": "Contact sales",
    "paywall.back": "Use a different email",
    // GDPR
    "gdpr.body": "We use a single first-party cookie to remember your language and currency. No tracking pixels, no third-party analytics.",
    "gdpr.accept": "Accept",
    "gdpr.decline": "Decline",
    "gdpr.privacy": "Privacy",
    // benchmark licence
    "licence.pending": "Pending licence",
    "licence.public": "Public source",
    // shared
    "common.approx_ecb": "Approx · ECB",
    "common.source": "Source",
    "common.value": "Value",
    "common.field": "Field",
    "common.origin": "Origin",
    "common.override": "User override",
    "common.estimated": "Estimated",
  },
  sv: {
    "nav.compare": "Jämför två alternativ",
    "nav.single": "Enskild analys",
    "nav.start": "Starta analys",
    "hero.eyebrow": "Ett styrelserumssäkert beslutsverktyg",
    "hero.h1a": "Bevisa ROI:n för ett pressmeddelande",
    "hero.h1b": " — innan du spenderar en euro.",
    "hero.body": "En rigorös kalkylator som förvandlar mjuka PR-antaganden till en beslutsklar instrumentpanel. Live-intervall, känslighet, källbelagda benchmarks, ECB-uppbackad valuta och en AI-skriven ledningssammanfattning — i en delbar länk byggd för styrelserummet.",
    "hero.cta": "Starta en ny ROI-analys",
    "hero.compare": "Eller jämför två alternativ sida vid sida",
    "hero.see": "Se hur modellen fungerar",
    "hero.stat.inputs": "Snabbläge",
    "hero.stat.range": "Känslighetsintervall",
    "hero.stat.model": "Modelltypsväxel",
    "hero.stat.share": "Delbar länk",
    "calc.currency": "Valuta",
    "calc.generate": "Skapa rapport",
    "calc.generating": "Skapar…",
    "calc.section.model": "Modell",
    "calc.section.mode": "Läge",
    "calc.section.basics": "Kampanjgrunder",
    "calc.section.basics.sub": "Kontext för ROI-berättelsen",
    "calc.section.tc": "Trafik & konvertering",
    "calc.section.tc.sub": "De sex siffror som påverkar ROI mest",
    "calc.section.costs": "Kostnader",
    "calc.section.costs.sub": "Allt du spenderar på denna release",
    "calc.section.bench": "Benchmarks (avancerat)",
    "calc.section.bench.sub": "Skriv över förifyllda värden. Tomt = behåll benchmark.",
    "calc.results.title": "Live-resultat",
    "calc.results.empty": "Ange dina värden för att se ROI-prognosen.",
    "calc.roi": "ROI (grundfall)",
    "calc.net": "Nettovinst",
    "calc.payback": "Återbetalning",
    "calc.total_benefit": "Total nytta",
    "calc.total_cost": "Total kostnad",
    "calc.range": "Intervall (±30% flex)",
    "calc.benefits": "Nyttofördelning",
    "calc.drivers": "Främsta värdedrivare",
    "report.layer1": "Poängkort",
    "report.layer1.sub": "Läs detta på 15 sekunder",
    "report.layer2": "Detaljer för ekonomi",
    "report.layer2.sub": "Källbelagda benchmarks · ECB-valuta · antaganden",
    "report.scorecard.cost": "Kostnadseffektivitet",
    "report.scorecard.sov": "Andel av röst",
    "report.scorecard.reach": "Medieräckvidd",
    "report.exec": "Ledningssammanfattning",
    "report.recommendation": "Rekommendation",
    "report.benefits": "Nyttofördelning",
    "report.benefits.h": "Var värdet kommer ifrån",
    "report.sensitivity": "Känslighetsanalys",
    "report.sensitivity.h": "Konservativ → Grund → Optimistisk",
    "report.assumptions": "Antaganden",
    "report.assumptions.h": "Alla värden & benchmarks som använts",
    "report.drivers": "Topp 3 värdedrivare",
    "report.sensitive": "Mest känsliga antaganden",
    "report.copy": "Kopiera länk",
    "report.print": "Skriv ut",
    "report.email": "E-posta rapport",
    "report.nordic_fallback": "Nordisk-specifik data väntar på licens — använder europeiskt medelvärde som fallback.",
    "email.title": "Vart ska vi skicka rapporten?",
    "email.body": "Vi sparar varje analys till en delbar länk. Lägg till din e-post för att få rapporten och möjliggöra leverans till intressenter.",
    "email.placeholder": "du@företag.se",
    "email.confirm": "Skapa & spara",
    "email.skip": "Hoppa över — visa bara länken",
    "email.quota": "Gratisnivå · {remaining} av {limit} rapporter kvar",
    "paywall.title": "Gratisnivå uppnådd",
    "paywall.body": "Du har använt {used} av {limit} gratisrapporter för denna e-post. Uppgradera för att fortsätta analysera.",
    "paywall.cta": "Kontakta sälj",
    "paywall.back": "Använd en annan e-post",
    "gdpr.body": "Vi använder en enda förstapartscookie för att komma ihåg ditt språk- och valutaval. Inga spårare. Inga tredjepartsanalyser.",
    "gdpr.accept": "Godkänn",
    "gdpr.decline": "Avböj",
    "gdpr.privacy": "Integritet",
    "licence.pending": "Väntar på licens",
    "licence.public": "Öppen källa",
    "common.approx_ecb": "Ungefärlig · ECB",
    "common.source": "Källa",
    "common.value": "Värde",
    "common.field": "Fält",
    "common.origin": "Ursprung",
    "common.override": "Överskriven",
    "common.estimated": "Uppskattad",
  },
};

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem("prid-lang");
    if (saved && DICT[saved]) return saved;
    const l = (navigator.language || "en").toLowerCase();
    return l.startsWith("sv") ? "sv" : "en";
  });

  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("prid-lang", lang);
  }, [lang]);

  const t = useCallback((key, vars) => {
    let str = (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key;
    if (vars) for (const [k, v] of Object.entries(vars)) str = str.replaceAll(`{${k}}`, String(v));
    return str;
  }, [lang]);

  // Stable context value — memoise so consumers only re-render when lang or t change
  const value = useMemo(() => ({ lang, setLang: setLangState, t }), [lang, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
